import itemListTemplate from './itemList.html';

/*******************************************************************************
 * @ngdoc overview
 * @name  app.itemList
 * @description
 * angular-ui-grid component to display list of deposit receipt items. Items
 * include both undeposited receipts and quick deposit items. The totals of the
 * items in the grid are maintained automatically in a **`receiptTotalsBlock`**
 * under the grid. This component is used in two ways:
 *
 *   1. In the main UI to list the items in the deposit. Items include Receipts
 *      and Quick Items which can be added to the deposit. This grid does not
 *      include the selection column and Quick Deposit Items show Change/Drop
 *      buttons in the **`Date`** column.
 *      Totals include all items in the grid.
 *
 *   2. In **`receiptsDialog`** to show the list of undeposited receipts and
 *      allow the user to select receipts to include in the deposit.
 *      Totals include only the selected receipts.
 *
 * Each receipt includes a list of **Payment Items**, one for each **Cash**,
 * **Check**, **Refund**, and **Change** item, and **Sales Item Totals**, giving
 * revenue totals by account. These are multi-line columns requiring the
 * grid to allow variable row heights (see below). The **Payment Items** and
 * **Sales Item Totals** columns are mutually exclusive and the
 * **`Payments/Details`** button groups switches between columns displayed.
 *
 * ------------------------
 *
 * The default behavior of **`angular-ui-grid`** has been modified.
 *
 *   * Custom CSS is used to provide variable row heights which is not supported.
 *     see: <https://github.com/angular-ui/ui-grid/issues/3239>.
 *
 *   * Default selection column is not in the same grid as the data. The entire
 *     column is in a DIV to the left of the data DIV and the row height is
 *     determined by by the static [**`GridOptions.rowHeight`**] [1] property.
 *     Since the column in the selection DIV is not part of the row in the data
 *     DIV, the cell height is not the same as the data DIV row height.
 *
 *     This grid disables the default selection column and adds one to the grid,
 *     using the default selection column templates. Unfortunately, this column
 *     cannot be pinned as this puts the column in the selection DIV, resulting
 *     in the same cell sizing issue.
 *
 *   * The default selection column templates are replaced with templates that
 *     use a checkbox input for selection.
 *
 * [1]: http://ui-grid.info/docs/#!/api/ui.grid.class:GridOptions#properties_rowheight
 *******************************************************************************/
/*******************************************************************************
 * itemList */
/**
 * @ngdoc component
 * @name  app.itemList.component:itemList
 * @param {Expr}    onLoad        Controller load complete action. `$itemList` is controller reference.
 * @param {Array}   items         Receipt/Quick items to load into grid.
 * @param {Boolean} forSelection  `true` for selecting undeposited receipts. `false` for deposit items.
 * @param {Array}   selectedIds   IDs of receipts to mark as selected if `forSelection == true`.
 * @param {Boolean} hideButtons
 * @param {Boolean} lockChanges
 * @description
 * angular-ui-grid component to display list of deposit receipt items.
 *
 *   * `selectable` - When `true`, the row selection column is inserted as the
 *     first header includes a checkbox to select all rows, and the totals
 *     include only the selected rows. `selectedIds` lists the `id` column values
 *     in `receiptList` to mark as selected.
 *     When `false`, no column is added and the totals include all rows.
 *
 *   * `onLoad` - Executed after the controller is fully loaded. `$itemList` is
 *     the controller reference.
 *******************************************************************************/
export const itemListComponent = {
  bindings: {
    onLoad: '&',
    items: '<',
    forSelection: '<',
    selectedIds: '<',
    hideButtons: '<',
    lockChanges: '='
  },
  template: itemListTemplate,
  controller: controller
}

/*******************************************************************************
 * controller */
/**
 * @ngdoc       function
 * @name        app.itemList.itemList.controller:controller
 * @description Controller for **itemList** component.
 *
 * @property {Number}   checksCount    Total number of checks.
 * @property {Number}   totalChecks    Total amount of checks.
 * @property {Number}   totalCash      Total amount of cash.
 * @property {Number}   totalAdjust    Total adjustments.
 * @property {Number}   totalDeposit   Total deposit.
 * @property {Object}   grid           `ui-grid` reference.
 * @property {Boolean}  showDetails    `true`  = Show account totals columns.
 *                                     `false` = Show payment item columns.
 *******************************************************************************/
/* @ngInject */ function controller($injector, $scope) {
  var $ctrl = this;
  $ctrl.showDetails = false;
  $ctrl.adjustTotals = $injector.invoke(adjustTotals, null, {
    $ctrl: $ctrl,
    $scope: $scope
  });
  $ctrl.dropQuickItem = $injector.invoke(dropQuickItem, null, {
    $ctrl: $ctrl,
    $scope: $scope
  });
  $ctrl.saveQuickItem = $injector.invoke(saveQuickItem, null, {
    $ctrl: $ctrl,
    $scope: $scope
  });
  /**
   * @ngdoc       function
   * @name        clearTotals
   * @methodOf    app.itemList.itemList.controller:controller
   * @description Clears item totals.
   */
  $ctrl.clearTotals = function () {
    $ctrl.checksCount = 0;
    $ctrl.totalChecks = 0;
    $ctrl.totalCash = 0;
    $ctrl.totalAdjust = 0;
    $ctrl.totalDeposit = 0;
  };
  $ctrl.clearTotals();

  /**
   * @ngdoc       function
   * @name        $onChanges
   * @methodOf    app.itemList.itemList.controller:controller
   * @description Reset data provider and totals when `items` changes.
   */
  $ctrl.$onChanges = function (changes) {
    if ($ctrl.grid && changes.items) $ctrl.grid.data = $ctrl.items;
  };

  /**
   * @ngdoc       function
   * @name        getSelectedItems
   * @methodOf    app.itemList.itemList.controller:controller
   * @description Returns the list of currently selected rows.
   */
  $ctrl.getSelectedItems = function () {
    return $ctrl.grid.api.selection.getSelectedRows();
  };

  /*-----------------------------------------------------------------*/
  /* Build the grid and notify parent once bindings are available.   */
  /* Bindings are not pre-assigned in the constructor in Angular 1.6+ */
  /* so this must run in $onInit.                                     */
  /*-----------------------------------------------------------------*/
  $ctrl.$onInit = function () {
    $ctrl.grid = $injector.invoke(buildGrid, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    if ($ctrl.items) $ctrl.grid.data = $ctrl.items;
    if ($ctrl.onLoad) $ctrl.onLoad({ $itemList: $ctrl });
  };
}

/*******************************************************************************
 * controller.adjustTotals */
/**
 * @ngdoc       function
 * @name        adjustTotals
 * @methodOf    app.itemList.itemList.controller:controller
 * @param       {Object}  item   Item info object.
 * @param       {Boolean} isAdd  `true` == **Add**, `false` == **Subtract**.
 * @description Adds or removes an from the list totals.
 *******************************************************************************/
/* @ngInject*/ function adjustTotals($ctrl, Tools) {
  return function (item, isAdd) {
    var totals = item.payments.reduce(
      function (totals, payment) {
        if (payment.isAdjustment) totals.totalAdjust += payment.amount;
        else if (payment.isCash) totals.totalCash += payment.amount;
        else {
          totals.totalChecks += payment.amount;
          totals.checksCount++;
        }
        return totals;
      },
      { checksCount: 0, totalChecks: 0, totalCash: 0, totalAdjust: 0 }
    );

    if (!isAdd) {
      totals.checksCount = -totals.checksCount;
      totals.totalChecks = -totals.totalChecks;
      totals.totalCash = -totals.totalCash;
      totals.totalAdjust = -totals.totalAdjust;
    }

    $ctrl.checksCount = Tools.roundMoney(
      $ctrl.checksCount + totals.checksCount
    );
    $ctrl.totalChecks = Tools.roundMoney(
      $ctrl.totalChecks + totals.totalChecks
    );
    $ctrl.totalCash = Tools.roundMoney($ctrl.totalCash + totals.totalCash);
    $ctrl.totalAdjust = Tools.roundMoney(
      $ctrl.totalAdjust + totals.totalAdjust
    );

    $ctrl.totalDeposit = Tools.roundMoney(
      $ctrl.totalChecks + $ctrl.totalCash + $ctrl.totalAdjust
    );
  };
}

/*******************************************************************************
 * controller.dropQuickItem */
/**
 * @ngdoc       function
 * @name        dropQuickItem
 * @methodOf    app.itemList.itemList.controller:controller
 * @param       {Object}  $item  Quick item info.
 * @description Drop Quick Deposit Item handler.
 *******************************************************************************/
/* @ngInject */ function dropQuickItem($ctrl, Alert) {
  return function ($item) {
    Alert.confirm('Do you want to Remove this Item?').then(function () {
      $ctrl.items.splice([$ctrl.items.indexOf($item)]);
      $ctrl.adjustTotals($item, false);
    });
  };
}

/*******************************************************************************
 * controller.saveQuickItem */
/**
 * @ngdoc       function
 * @name        saveQuickItem
 * @methodOf    csm.books.MakeDeposit.makeDeposit.controller:controller
 * @param       {Object}  $orig  Original item info, `null` for new item.
 * @param       {Object}  $item  New/Updated item info.
 * @return      {Promise} Resolved when item is updated.
 * @description Save Quick Deposit Item handler.
 *******************************************************************************/
/* @ngInject */ function saveQuickItem($ctrl, $q) {
  return function ($orig, $item) {
    if ($orig) {
      $ctrl.items[$ctrl.items.indexOf($orig)] = $item;
      $ctrl.adjustTotals($orig, false);
    } else $ctrl.items.push($item);

    $ctrl.adjustTotals($item, true);
    return $q.resolve();
  };
}

/*******************************************************************************
 * buildGrid */
/**
 * This grid contains a single row for each receipt. Payments and Sales Item
 * Total columns are mutually exclusive and contain one line of data for each
 * Payment or Sales Item Total item in the receipt.
 *
 * angular-ui-grid selection column is not in the same grid as the data. The
 * entire column is in a DIV to the left of the data and the row height is
 * determined by by the static 'rowHeight' property. There is no way to set the
 * cell height dynamically based on the height of the data row so we have to
 * create our own selection column in the data grid and turn off the default.
 *******************************************************************************/
/* @ngInject */ function buildGrid(
  $ctrl,
  $scope,
  $templateCache,
  uiGridConstants,
  $injector,
  SERVER
) {
  /*------------------------------*/
  /* Configure receipt item grid. */
  /*------------------------------*/
  var gridOptions = {
    columnDefs: [],
    enableColumnMenus: false,
    enableFiltering: false,
    enableFullRowSelection: true,
    enableSelectAll: false,
    enableSelectionBatchEvent: false,
    multiSelect: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,

    /*------------------------------------------------------------*/
    /* Turn off the row selection column since we create our own. */
    /*------------------------------------------------------------*/
    enableRowSelection: true,
    enableRowHeaderSelection: false,

    /*---------------------------------------------------*/
    /* `registerAPI` adds a method to `apiRegistrations` */
    /* and `onRegisterApi` executes them.                */
    /*---------------------------------------------------*/
    apiRegistrations: [],
    registerAPI: function (f) {
      this.apiRegistrations.push(f);
      return true;
    },
    onRegisterApi: function (api) {
      this.api = api;
      this.apiRegistrations.forEach(function (f) {
        f(api);
      });
    }
  };

  /*-------------------------------------------------------------------------------*/
  /* It turns out there is no mechanism for pre-selecting rows or running a        */
  /* process after the `data` property has changed. Some suggested using           */
  /* `api.grid.modifyRows()` but this is not reliable because the grid is not      */
  /* created there. It is created in `dataWatchFunction` which will dispatch a ROW */
  /* data change event after data has been changed and the grid has been built.    */
  /*                                                                               */
  /* `data` Property - Sets/Returns `rawData`. Setter calls `resetTotals()` to     */
  /* reset totals from the loaded items.                                           */
  /*                                                                               */
  /* If `onRegisterApi()` has not been executed, the API is not available and the  */
  /* grid has not been configured. `resetTotals()` is called in `onRegisterAPI()`. */
  /*-------------------------------------------------------------------------------*/
  Object.defineProperty(gridOptions, 'data', {
    get: function () {
      return this.rawData || (this.rawData = []);
    },
    set: function (data) {
      this.rawData = data;
      if (this.api) this.resetTotals(this);
    }
  });

  /*------------------------------------------------------*/
  /* Update the totals for the initial setting of `data`. */
  /*------------------------------------------------------*/
  gridOptions.registerAPI(function (api) {
    api.grid.options.resetTotals(api.grid.options);
  });

  /*-------------------------------------------------------*/
  /* `resetTotals` is called after the `data` property has */
  /* been updated and waits for a ROW data change event.   */
  /*-------------------------------------------------------*/
  gridOptions.resetTotals = function (self) {
    /*-------------------------------------------*/
    /* Register handler to reset totals on next  */
    /* ROW event and deregister after execution. */
    /*-------------------------------------------*/
    var deregister = self.api.grid.registerDataChangeCallback(
      function (grid) {
        $ctrl.clearTotals();

        /*--------------------------------------------*/
        /* If this grid is not for receipt selection, */
        /* accumulate totals for all items.           */
        /*--------------------------------------------*/
        if (!$ctrl.forSelection)
          self.data.some(function (item) {
            $ctrl.adjustTotals(item, true);
          });
        else if (Array.isArray($ctrl.selectedIds))
          self.data.some(function (item) {
            if (
              $ctrl.selectedIds.some(function (id) {
                return id === item.id;
              })
            )
              self.api.selection.selectRow(item);
          });
        deregister();
      },
      [uiGridConstants.dataChange.ROW]
    );
  };

  /*--------------------------------------*/
  /* Selectable grid has selection column */
  /* and totals only the selected items.  */
  /*--------------------------------------*/
  if ($ctrl.forSelection) {
    gridOptions.enableSelectAll = true;
    gridOptions.multiSelect = true;

    /*------------------------------------------------*/
    /* Use the header/cell templates from the ui-grid */
    /* selection column for our replacement column.   */
    /*------------------------------------------------*/
    gridOptions.columnDefs.push({
      field: 'selected',
      width: 30,
      headerCellTemplate: $templateCache.get('ui-grid/selectionHeaderCell'),
      cellTemplate: $templateCache.get('ui-grid/selectionRowHeader')
    });

    /*----------------------------------------*/
    /* Add selection change handler to adjust */
    /* totals when row is (de)selected.       */
    /*----------------------------------------*/
    gridOptions.registerAPI(function (api) {
      api.selection.on.rowSelectionChanged($scope, function (row) {
        $ctrl.adjustTotals(row.entity, row.isSelected);
      });
    });
  }

  /*---------------------------------------------*/
  /* Date column shows receipt date for          */
  /* receipts and change button for quick items. */
  /*---------------------------------------------*/
  gridOptions.columnDefs.push({
    field: 'receiptDate',
    width: 90,
    displayName: 'Date',
    type: 'date',
    cellFilter: "date:'MM/dd/yyyy'",
    filterCellFiltered: true,
    cellTemplate: $templateCache.get('item-list/date-column')
  });

  /*------------------------------*/
  /* Receipt Number/From columns. */
  /*------------------------------*/
  gridOptions.columnDefs.push(
    {
      field: 'receiptNumber',
      width: 100,
      displayName: 'Rcpt No',
      type: 'number'
    },
    {
      field: 'receiptFrom',
      minWidth: 200,
      displayName: 'From',
      type: 'string'
    }
  );

  /*----------------------------------------------------------------*/
  /* Payments/Details columns are visible based on                  */
  /* Payments/Details' button setting. Methods to set columns       */
  /* visible are set as 'onRefresh' property on column definition   */
  /* and called when grid is refreshed by custom columns processor. */
  /*                                                                */
  /* These are multi-link columns and use the `listColumn`          */
  /* directive in the cell template.                                */
  /*----------------------------------------------------------------*/
  var showPayments = function () {
    this.visible = !$ctrl.showDetails;
  };
  var showDetails = function () {
    this.visible = $ctrl.showDetails;
  };
  var paymentsTemplate = '<list-column name="payments"/>';
  var saleItemsTemplate = '<list-column name="saleItemTotals"/>';

  gridOptions.registerAPI(function (api) {
    api.core.registerColumnsProcessor(function (cols) {
      for (var i = 0; i < cols.length; i++)
        if (cols[i].colDef.onRefresh) cols[i].colDef.onRefresh.call(cols[i]);
      return cols;
    });
  });

  /*---------------------------------------------*/
  /* Build 'sortingAlgorithm' handlers for       */
  /* multi-row columns. Handlers use 'field' and */
  /* 'type' properties to determine sort values. */
  /*---------------------------------------------*/
  var saPayments = $injector.invoke(listColumnCompare, null, {
    listName: 'payments'
  });
  var saDetails = $injector.invoke(listColumnCompare, null, {
    listName: 'saleItemTotals'
  });

  /*----------------------------------------*/
  /* Sugar for adding list columns to grid. */
  /*----------------------------------------*/
  var lcPayments = function (props) {
    return Object.assign(
      Object.create({
        onRefresh: showPayments,
        cellTemplate: paymentsTemplate,
        sortingAlgorithm: saPayments
      }),
      props
    );
  };
  var lcDetails = function (props) {
    return Object.assign(
      Object.create({
        onRefresh: showDetails,
        cellTemplate: saleItemsTemplate,
        sortingAlgorithm: saDetails
      }),
      props
    );
  };

  gridOptions.columnDefs.push(
    lcPayments({
      field: 'paymentDisplay',
      width: 100,
      displayName: 'Method',
      type: 'string'
    }),
    lcPayments({
      field: 'checkNumber',
      width: 100,
      displayName: 'No',
      type: 'string'
    }),
    lcPayments({
      field: 'checkName',
      minWidth: 200,
      displayName: 'Name on Check',
      type: 'string'
    }),
    lcPayments({
      field: 'amount',
      width: 100,
      displayName: 'Amount',
      type: 'number',
      cellFilter: "currency:''",
      cellClass: 'text-right'
    }),
    lcDetails({
      field: 'description',
      minWidth: 200,
      displayName: 'Desc',
      type: 'string'
    })
  );

  /*----------------------------------*/
  /* Add account number/name columns. */
  /*----------------------------------*/
  var acctNum = SERVER.BooksWebConstants.shortAccountNumber;
  var acctName = SERVER.BooksWebConstants.shortAccountName;

  if (SERVER.BooksWebConstants.showAcctNumber)
    gridOptions.columnDefs.push(
      lcDetails({
        field: 'accountNumber',
        minWidth: 100,
        displayName: acctNum,
        type: 'string'
      })
    );

  gridOptions.columnDefs.push(
    lcDetails({
      field: 'accountName',
      minWidth: 200,
      displayName: acctName,
      type: 'string'
    }),
    lcDetails({
      field: 'amount',
      width: 100,
      displayName: 'Amount',
      type: 'number',
      cellFilter: "currency:''",
      cellClass: 'text-right'
    })
  );

  return gridOptions;
}

listColumnDirective.$inject = [];

/*******************************************************************************
 * listColumn */
/**
 * @ngdoc    directive
 * @name     csm.books.MakeDeposit.directive:listColumn
 * @restrict E
 * @param    {String} name  List property name.
 * @description
 * Cell template for fields in the payments/saleItem arrays. Template calls a
 * method to generate the multiple row data as an alternative to ng-repeat which
 * is very slow. Built as a directive so a new scope is not created.
 *
 * Note: This directive needs `display:block` styling to work correctly in IE.
 *******************************************************************************/
export function listColumnDirective() {
  controller.$inject = ['$sce', '$scope', '$parse', '$attrs'];

  /* @ngInject */ function controller($sce, $scope, $parse, $attrs) {
    var nullAsEmpty = function (v) {
      return !(angular.isUndefined(v) || v === null) ? v : '';
    };

    $scope.makeMultiColumn = function (col, row) {
      return $sce.trustAsHtml(
        row[$attrs.name]
          .map(function (item) {
            var val = col.cellFilter
              ? $parse('item[col.field] | ' + col.cellFilter)({
                  item: item,
                  col: col
                })
              : item[col.field];
            return '<div class="{0}">{1}</div>'.format(
              nullAsEmpty(col.cellClass),
              nullAsEmpty(val)
            );
          })
          .join('')
      );
    };
  }

  return {
    restrict: 'E',
    template:
      '<div class="ui-grid-cell-contents" ng-bind-html="makeMultiColumn(col.colDef, row.entity)"></div>',
    controller: controller
  };
}

/*******************************************************************************
 * listColumnCompare */
/**
 * Returns a `sortingAlgorithm` compare function for use in sorting the
 * multi-row columns. `listName` is the property name for the array containing
 * the row data. `field` and `type` properties from the column definition
 * identify the array property for column data and the type of sort to perform.
 * The array from each row is sorted in place according to the sort direction.
 * Finally, the `field` value from the first element of each array is compared
 * and the result returned.
 *
 * Note: The data provider is modified by the sorting of the `listName` arrays.
 *******************************************************************************/
/* @ngInject */ function listColumnCompare(rowSorter, listName) {
  return function (a, b, ra, rb, dir, col) {
    var cf = rowSorter.guessSortFn(col.colDef.type);
    var fn = col.colDef.field;
    var sf = function (e1, e2) {
      return dir === 'asc' ? cf(e1[fn], e2[fn]) : cf(e2[fn], e1[fn]);
    };

    var va = ra.entity[listName].sort(sf).slice().shift() || {};
    var vb = rb.entity[listName].sort(sf).slice().shift() || {};

    return cf(va[fn], vb[fn]);
  };
}
